// Builds the parameter panel from the schema in params.ts.
import { PARAMS, type Mode, type ParamDef, type Values } from "../params";

export function buildControls(root: HTMLElement, mode: Mode, values: Values, onChange: (key: string) => void) {
  root.innerHTML = "";
  const groups = new Map<string, ParamDef[]>();
  for (const p of PARAMS[mode]) {
    if (!groups.has(p.group)) groups.set(p.group, []);
    groups.get(p.group)!.push(p);
  }
  const hints: (() => void)[] = [];
  for (const [name, defs] of groups) {
    const g = document.createElement("div");
    g.className = "group";
    g.innerHTML = `<h4>${name}</h4>`;
    for (const p of defs) g.appendChild(control(p, values, () => { hints.forEach((h) => h()); onChange(p.key); }, hints));
    root.appendChild(g);
  }
  const reset = document.createElement("button");
  reset.textContent = "Reset to original defaults";
  reset.style.marginTop = "16px";
  reset.onclick = () => {
    for (const p of PARAMS[mode]) values[p.key] = p.def;
    buildControls(root, mode, values, onChange);
    onChange("*");
  };
  root.appendChild(reset);
}

function control(p: ParamDef, values: Values, changed: () => void, hints: (() => void)[]): HTMLElement {
  const row = document.createElement("div");
  row.className = "param";
  const scope = p.scope === "both" ? "" : `<span class="scope ${p.scope}">${p.scope}</span>`;
  const help = `<span class="help" title="${p.help.replace(/"/g, "&quot;")}">?</span>`;
  const label = document.createElement("label");
  label.innerHTML = `${p.label} ${scope} ${help}`;
  row.appendChild(label);
  const ctl = document.createElement("div");
  ctl.className = "ctl";
  row.appendChild(ctl);

  if (typeof p.def === "boolean") {
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.checked = !!values[p.key];
    cb.onchange = () => { values[p.key] = cb.checked; changed(); };
    ctl.appendChild(cb);
  } else if (p.options) {
    const sel = document.createElement("select");
    for (const o of p.options) sel.add(new Option(o.label, o.value));
    sel.value = String(values[p.key]);
    sel.onchange = () => { values[p.key] = sel.value; changed(); };
    ctl.appendChild(sel);
  } else {
    const num = document.createElement("input");
    num.type = "number";
    num.min = String(p.min); num.max = String(p.max); num.step = String(p.step ?? "any");
    num.value = String(values[p.key]);
    const unit = document.createElement("span");
    unit.className = "muted small";
    unit.textContent = p.unit ?? "";
    ctl.append(num, unit);
    const range = document.createElement("input");
    range.type = "range";
    range.min = String(p.min); range.max = String(p.max); range.step = String(p.step ?? "any");
    range.value = String(values[p.key]);
    row.appendChild(range);
    const set = (x: number, from: "num" | "range" | "preset") => {
      if (!Number.isFinite(x)) return;
      values[p.key] = x;
      if (from !== "num") num.value = String(x);
      if (from !== "range") range.value = String(x);
      changed();
    };
    num.onchange = () => set(Number(num.value), "num");
    range.oninput = () => set(Number(range.value), "range");
    if (p.presets) {
      const pr = document.createElement("div");
      pr.className = "presets";
      for (const x of p.presets) {
        const b = document.createElement("button");
        b.textContent = String(x);
        b.onclick = () => set(x, "preset");
        pr.appendChild(b);
      }
      row.appendChild(pr);
    }
  }
  if (p.hint) {
    const h = document.createElement("div");
    h.className = "hint";
    const upd = () => (h.textContent = `= ${p.hint!(values)}`);
    upd();
    hints.push(upd);
    row.appendChild(h);
  }
  return row;
}
